/**
 * 언어 상태 관리 스토어
 * 현재 선택된 언어 정보를 전역적으로 관리합니다.
 */

import { ref, computed } from 'vue'
import { getSupportedLanguages, type LanguageMetadata } from '@/utils/locales/messages'
import { syncTutorialLanguage } from '@/shared/utils/tutorialLang'

// 현재 언어 상태
const currentLanguage = ref<string>('en')

// 지원 언어 목록
const supportedLanguages = computed(() => getSupportedLanguages())

// i18n 인스턴스 참조
let i18nInstance: any = null

/**
 * i18n 인스턴스 설정
 */
const setI18nInstance = (instance: any) => {
  i18nInstance = instance
}

/**
 * 언어 변경 함수
 * @param languageCode - 변경할 언어 코드 (예: 'ko', 'en', 'zh-cn')
 */
const setLanguage = (languageCode: string): void => {
  console.log('🌐 Setting language to:', languageCode)
  console.log('🌐 Supported languages:', supportedLanguages.value.map(l => l.code))
  
  // tw를 zh-tw로 매핑 (일반적인 오타 처리)
  let actualLanguageCode = languageCode
  if (languageCode === 'tw') {
    actualLanguageCode = 'zh-tw'
    console.log('🌐 Mapped tw to zh-tw')
  }
  
  // 지원되는 언어인지 확인
  const isValidLanguage = supportedLanguages.value.some(lang => lang.code === actualLanguageCode)
  
  if (!isValidLanguage) {
    console.warn(`Unsupported language code: ${languageCode} (mapped to: ${actualLanguageCode}). Falling back to English.`)
    currentLanguage.value = 'en'
    return
  }
  
  // 실제 언어 코드 사용
  languageCode = actualLanguageCode
  
  currentLanguage.value = languageCode

  // localStorage에 저장 (먼저)
  localStorage.setItem('preferred-language', languageCode)

  // 튜토리얼/가이드 HTML 언어(lt_lang)도 함께 동기화
  syncTutorialLanguage(languageCode)

  // i18n locale도 즉시 동기화 (번역 데이터는 initializeTranslations에서 앱 마운트 전에 전체 로드됨)
  if (i18nInstance) {
    try {
      i18nInstance.locale.value = languageCode
    } catch (e) {
      // silent
    }
  }

  // 언어 변경 이벤트 발생 (번역 로드 트리거 — 운영 환경 동적 로드용)
  window.dispatchEvent(new CustomEvent('language-changed', {
    detail: { language: languageCode }
  }))

  console.log(`🌐 Language changed to: ${languageCode}`)
}

/**
 * 현재 언어 정보 가져오기
 */
const getCurrentLanguage = (): string => {
  return currentLanguage.value
}

/**
 * 현재 언어의 메타데이터 가져오기
 */
const getCurrentLanguageMetadata = (): LanguageMetadata | undefined => {
  return supportedLanguages.value.find(lang => lang.code === currentLanguage.value)
}

/**
 * 언어 초기화 (main.ts에서 호출됨)
 * localStorage에서 저장된 언어를 불러와 i18n과 동기화
 */
const initializeLanguage = (): void => {
  const savedLanguage = localStorage.getItem('preferred-language')

  // 앱 시작 시점에 튜토리얼 언어(lt_lang)를 현재 앱 언어와 맞춰둠
  syncTutorialLanguage(savedLanguage || currentLanguage.value)

  if (savedLanguage) {
    currentLanguage.value = savedLanguage

    // i18n locale 설정
    if (i18nInstance) {
      try {
        i18nInstance.locale.value = savedLanguage
        console.log(`🌐 i18n locale initialized to: ${savedLanguage}`)
      } catch (error) {
        console.warn('Failed to initialize i18n locale:', error)
      }
    }

    console.log(`🌐 Language initialized from localStorage: ${savedLanguage}`)
  } else {
    // localStorage에 저장된 값이 없으면 기본값 사용 (main.ts에서 설정)
    console.log('🌐 No saved language found, using default from main.ts')
  }
}

/**
 * 언어 변경 리스너 등록
 */
const onLanguageChange = (callback: (language: string) => void): void => {
  window.addEventListener('language-changed', (event: any) => {
    callback(event.detail.language)
  })
}

export const useLanguageStore = () => {
  return {
    // 상태
    currentLanguage: computed(() => currentLanguage.value),
    supportedLanguages,
    
    // 메서드
    setLanguage,
    getCurrentLanguage,
    getCurrentLanguageMetadata,
    onLanguageChange,
    setI18nInstance,
  }
}

// 기본 export
export default {
  currentLanguage: computed(() => currentLanguage.value),
  supportedLanguages,
  setLanguage,
  getCurrentLanguage,
  getCurrentLanguageMetadata,
  onLanguageChange,
  setI18nInstance,
}

